
    import { cloudinaryUrl } from './cloudinary.js'
    import { initIris } from './iris.js'
    initIris()

    let clients = []
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    Promise.all([
      fetch('/data/design.json').then(r => r.json()),
      fetch('/data/gallery.json').then(r => r.json()).catch(() => []),
      fetch('/data/videos.json').then(r => r.json()).catch(() => [])
    ]).then(([design, gallery, videos]) => {
      clients = buildClients(design, gallery.filter(i => i.type === '3d'), videos)
      if (!clients.length) return

      const tabsEl = document.getElementById('designTabs')
      const types  = [...new Set(clients.flatMap(c => c.projects.map(p => p.workType).filter(Boolean)))]
      // Keep the broad 3D / Motion filters together at the end of the row
      const last   = ['3D Design', 'Motion Design']
      types.sort((a, b) => last.includes(a) - last.includes(b))

      tabsEl.innerHTML = ['all', ...types].map(t => `
        <button class="category-tab${t === 'all' ? ' active' : ''}" data-type="${t}">${t === 'all' ? 'All' : t}</button>
      `).join('')

      tabsEl.addEventListener('click', e => {
        const btn = e.target.closest('.category-tab')
        if (!btn) return
        tabsEl.querySelectorAll('.category-tab').forEach(b => b.classList.remove('active'))
        btn.classList.add('active')
        renderCases(btn.dataset.type)
      })

      renderCases('all')
    }).catch(() => {})

    // Merge 3D renders (gallery.json) and videos (videos.json) into the
    // design.json clients they're tagged with via "client": "<id>".
    // Anything untagged goes into a closing "Studio & personal work" section.
    function buildClients(design, items3d, videos) {
      const studio = { id: 'studio', client: 'Studio & personal work', tagline: '3D and motion pieces made outside the client work above', projects: [], unnumbered: true }
      const byId   = Object.fromEntries(design.map(c => [c.id, { ...c, projects: [...(c.projects || [])] }]))
      const home   = id => byId[id] || studio

      // 3D renders sharing a client + title become one project with a gallery
      items3d.forEach(item => {
        const client  = home(item.client)
        const project = client.projects.find(p => p.image && p.title === item.title)
        if (project) {
          project.gallery = [...(project.gallery || []), item.url]
        } else {
          client.projects.push({ image: item.url, title: item.title, workType: '3D Design' })
        }
      })

      videos.forEach(v => {
        if (!v.cloudinaryUrl && !v.youtubeId) return
        home(v.client).projects.push({ video: v.cloudinaryUrl, youtubeId: v.youtubeId, aspect: v.aspect, title: v.title, workType: 'Motion Design' })
      })

      return [...Object.values(byId), studio].filter(c => c.projects.length)
    }

    function renderCases(type) {
      const listEl = document.getElementById('caseList')
      let n = 0
      const visible = clients
        .map(client => ({
          client,
          number: client.unnumbered ? '' : String(++n).padStart(2, '0'),
          projects: type === 'all' ? client.projects : client.projects.filter(p => p.workType === type)
        }))
        .filter(c => c.projects.length)

      listEl.innerHTML = visible.length
        ? visible.map(caseStudy).join('')
        : '<p class="gallery-empty">Nothing here yet — check back soon.</p>'

      listEl.querySelectorAll('[data-case]').forEach(el => {
        el.addEventListener('click', () => {
          const images = visible[el.dataset.case].projects
            .filter(p => p.image)
            .flatMap(p => [p.image, ...(p.gallery || [])].map(url => ({ url, title: p.title })))
          window.__lightboxItems = images
          openLightbox(images.findIndex(i => i.url === el.dataset.url))
        })
      })

      initVideos(listEl)
    }

    function caseStudy({ client, number, projects }, caseIndex) {
      const [lead, ...rest] = projects
      const services = [...new Set(client.projects.map(p => p.workType).filter(Boolean))]
      return `
        <article class="case-study" id="${client.id}">
          <header class="case-head">
            <p class="case-number">${number}</p>
            <div>
              <h2 class="case-title">${client.client}</h2>
              ${client.tagline ? `<p class="case-tagline">${client.tagline}</p>` : ''}
            </div>
            <div class="case-meta">
              ${client.summary ? `<p class="case-summary">${client.summary}</p>` : ''}
              <ul class="case-services">${services.map(s => `<li>${s}</li>`).join('')}</ul>
            </div>
          </header>
          <div class="case-lead">
            ${projectMedia(lead, caseIndex, 'w_1600,q_auto,f_auto')}
            ${projectText(lead)}
          </div>
          ${rest.length ? `
            <div class="case-grid">
              ${rest.map(p => `
                <div class="case-project">
                  ${projectMedia(p, caseIndex, 'w_900,q_auto,f_auto')}
                  ${projectText(p)}
                </div>
              `).join('')}
            </div>
          ` : ''}
        </article>
      `
    }

    function projectMedia(p, caseIndex, transform) {
      if (p.video || p.youtubeId) return videoMedia(p)
      // Logos and other PNG artwork are shown whole; photos/mockups fill the frame
      const fit = /\.png$/i.test(p.image) ? ' case-media--contain' : ''
      const count = p.gallery ? `<span class="case-count">${p.gallery.length + 1} images</span>` : ''
      return `
        <button class="case-media${fit}" data-case="${caseIndex}" data-url="${p.image}" aria-label="View ${p.title || 'project'} larger">
          <img src="${cloudinaryUrl(p.image, transform)}" alt="${p.title || ''}" loading="lazy">
          ${count}
        </button>
      `
    }

    function videoMedia(p) {
      const [w, h] = (p.aspect || '16:9').split(':').map(Number)
      const ratio  = `aspect-ratio: ${w} / ${h};`
      if (p.youtubeId) {
        return `
          <div class="case-media case-media--video" style="${ratio}">
            <iframe src="https://www.youtube.com/embed/${p.youtubeId.split('?')[0]}" title="${p.title || ''}" loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
          </div>
        `
      }
      return `
        <div class="case-media case-media--video" style="${ratio}">
          <video data-src="${p.video}" muted loop playsinline preload="none"${reduceMotion ? ' controls' : ''} aria-label="${p.title || 'Motion piece'}"></video>
        </div>
      `
    }

    // Native videos load when scrolled near and play muted while on screen
    let videoObserver
    function initVideos(root) {
      if (videoObserver) videoObserver.disconnect()
      videoObserver = new IntersectionObserver(entries => {
        entries.forEach(({ target: el, isIntersecting }) => {
          if (isIntersecting) {
            if (!el.src) el.src = el.dataset.src
            if (!reduceMotion) el.play().catch(() => {})
          } else {
            el.pause()
          }
        })
      }, { rootMargin: '200px' })
      root.querySelectorAll('video[data-src]').forEach(el => videoObserver.observe(el))
    }

    function projectText(p) {
      return `
        <div class="case-text">
          ${p.workType    ? `<p class="case-type">${p.workType}</p>` : ''}
          ${p.title       ? `<h3>${p.title}</h3>` : ''}
          ${p.description ? `<p>${p.description}</p>` : ''}
        </div>
      `
    }

    let __lightboxIndex = 0
    window.openLightbox = function (index) {
      const items = window.__lightboxItems || []
      if (!items.length) return
      __lightboxIndex = index
      showLightboxItem(__lightboxIndex)
      document.getElementById('lightbox').classList.add('active')
    }
    function showLightboxItem(index) {
      const items = window.__lightboxItems || []
      if (!items.length) return
      __lightboxIndex = (index + items.length) % items.length
      const item = items[__lightboxIndex]
      document.getElementById('lightboxImg').src = cloudinaryUrl(item.url, 'w_1600,q_auto,f_auto')
      document.getElementById('lightboxImg').alt = item.title || ''
      document.getElementById('lightboxCaption').textContent = item.title || ''
    }
    window.stepLightbox = function (dir) { showLightboxItem(__lightboxIndex + dir) }
    window.closeLightbox = function () { document.getElementById('lightbox').classList.remove('active') }
    document.getElementById('lightbox').addEventListener('click', function (e) { if (e.target === this) closeLightbox() })
    document.addEventListener('keydown', e => {
      const lb = document.getElementById('lightbox')
      if (!lb.classList.contains('active')) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') stepLightbox(1)
      if (e.key === 'ArrowLeft') stepLightbox(-1)
    })

    document.addEventListener('contextmenu', e => { if (e.target.tagName === 'IMG') e.preventDefault() })
    document.addEventListener('dragstart', e => { if (e.target.tagName === 'IMG') e.preventDefault() })

    const navToggle   = document.querySelector('.nav-toggle')
    const mobileNav   = document.getElementById('mobileNav')
    const mobileClose = document.querySelector('.mobile-nav-close')
    navToggle.addEventListener('click', () => mobileNav.classList.add('open'))
    mobileClose.addEventListener('click', () => mobileNav.classList.remove('open'))
    document.querySelectorAll('.mobile-nav-link').forEach(l => l.addEventListener('click', () => mobileNav.classList.remove('open')))
  